import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Grid, Typography, Button, Alert, Tooltip, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PublicIcon from '@mui/icons-material/Public';
import CoronavirusOutlinedIcon from '@mui/icons-material/CoronavirusOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  getInspectorSummary, triggerInspectorRefresh, getInspectorRefreshJob,
  getInspectorActiveRefreshJob,
} from '../../../services/inspectorApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalErrorState, PortalEmptyState, PortalSectionCard,
} from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, statTileSx, statTileFooterSx, tableHeadRowSx,
  headCellSx, rowSx, bodyCellSx, statusChipSx, insetPanelSx, tileEntranceStyle,
} from '../portalStyles';
import { severityColor } from './severity';

// Inspector accent, kept identical to engines/inspector.js so every Inspector
// route (entry, setup, dashboard, findings, enrollment, settings) reads as one
// capability. Hardcoded rather than imported for parity with the sibling
// dashboards, which each carry their own engine constant.
const INSPECTOR_COLOR = '#7D3C98';
const INSPECTOR_COLOR_DARK = '#652F7D'; // one dark per engine, as Detective does — was #5B2C6F

// Inspector's OWN severity vocabulary. SEVERITY_ORDER is deliberately per-engine
// (Overview.js carries the union; Macie's omits CRITICAL) so do not sync this
// with the others.
//
// UNTRIAGED is Inspector-only and means "AWS has not yet assigned a severity" —
// it is NOT informational, which is why severity.js gives it its own colour
// rather than letting it fall back to informational grey.
//
// INFORMATIONAL is here even though Inspector rarely emits it, because
// SeverityCounts is keyed by whatever label AWS returned: a label present in the
// data but missing from this array would be counted in the total printed under
// the tiles yet invisible in the breakdown above it, which reads as a broken
// total. severityRowsFor() extends the same guarantee to a label AWS has not
// invented yet.
const SEVERITY_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL', 'UNTRIAGED'];

// Colours come from severity.js; this map only carries the icon and the hint
// shown on tiles whose label needs explaining.
const severityMeta = {
  CRITICAL: { icon: <ErrorOutlineIcon sx={{ fontSize: 28 }} /> },
  HIGH: { icon: <WarningAmberIcon sx={{ fontSize: 28 }} /> },
  MEDIUM: { icon: <WarningAmberIcon sx={{ fontSize: 28 }} /> },
  LOW: { icon: <InfoOutlinedIcon sx={{ fontSize: 28 }} /> },
  INFORMATIONAL: {
    icon: <InfoOutlinedIcon sx={{ fontSize: 28 }} />,
    hint: 'Reported for awareness — no vulnerability to remediate.',
  },
  UNTRIAGED: {
    icon: <HelpOutlineIcon sx={{ fontSize: 28 }} />,
    hint: 'AWS has not assigned a severity to these findings yet. Untriaged is not the same as informational — a critical vulnerability can sit here.',
  },
};

const inspectorContainedSx = {
  backgroundColor: INSPECTOR_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  px: 3,
  '&:hover': { backgroundColor: INSPECTOR_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const captionSx = {
  fontFamily: FONT_BODY,
  fontSize: '0.8rem',
  color: CK.muted,
  display: 'block',
};

// The unconfigured-tenant signal. GET /summary REFUSES to answer a tenant that
// has not enabled Inspector, so that "Inspector is off" cannot render like
// "Inspector is on and everything is clean".
//
// This is a STRING CONTRACT with two ends, and this constant is one of them: the
// other is services.ErrInspectorNotConfigured in the Go backend
// (backend/services/inspector_service.go), whose text reaches here verbatim
// through the handler's 404 body. Nothing pins the two together at build time,
// so a reword on either side must change both.
//
// Matching the message is the only lever available: the refusal really is a 404,
// but portalFetch discards the status and rejects with a bare Error carrying
// nothing but the backend's string. The whole sentinel is compared rather than a
// loose "not configured" fragment so a future error that merely mentions the
// phrase cannot be mistaken for this one.
const NOT_CONFIGURED_MESSAGE = 'Amazon Inspector is not configured';
const isNotConfigured = (e) =>
  String(e?.message || '').toLowerCase().includes(NOT_CONFIGURED_MESSAGE.toLowerCase());

// relativeTime returns a short "2 min ago" style string for a past ISO time.
const relativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.max(0, (Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} h ago`;
  return `${Math.round(diffSec / 86400)} d ago`;
};

// InspectorScore is 0 when AWS reported no score — NETWORK_REACHABILITY findings
// legitimately have none — so 0 means "absent", not "scored zero", and renders as
// an em dash rather than a reassuring 0.0.
const formatScore = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n.toFixed(1) : '—';
};

// TopCVEs[].fixAvailable is a rollup over the CVE's findings by the precedence
// YES > PARTIAL > NO, not a MAX, so PARTIAL genuinely means "some of these can be
// patched and some cannot".
const FIX_META = {
  YES: { intent: 'success', label: 'Yes', hint: 'At least one finding for this CVE has a vendor fix available.' },
  PARTIAL: { intent: 'warning', label: 'Partial', hint: 'Some findings for this CVE have a fix and some do not.' },
  NO: { intent: 'neutral', label: 'No', hint: 'No vendor fix is available for any finding of this CVE yet.' },
};

const numeric = (v) => Number(v || 0).toLocaleString();

// StatTile is the one tile shape both stat rows use, so the severity row and the
// coverage row are exactly the same height and read as one system. onClick is
// optional: a tile with no findings view to open must not offer a pointer.
function StatTile({ icon, value, color, label, caption, hint, onClick }) {
  const tile = (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        ...statTileSx,
        ...(onClick
          ? {
            cursor: 'pointer',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            '&:hover': { borderColor: color, boxShadow: `0 4px 12px ${color}20` },
          }
          : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '10px',
            backgroundColor: `${color}12`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontWeight: 700,
            fontSize: '2.5rem',
            color,
            lineHeight: 1,
            wordBreak: 'break-all',
          }}
        >
          {value}
        </Typography>
      </Box>
      <Box sx={statTileFooterSx}>
        <Typography
          sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '1rem', color: CK.navy, mb: 0.5 }}
        >
          {label}
        </Typography>
        <Typography sx={{ ...captionSx, fontWeight: 500 }}>{caption}</Typography>
      </Box>
    </Paper>
  );
  // MUI renders the child untouched when title is empty, so tiles without a hint
  // pay nothing for going through Tooltip.
  return <Tooltip title={hint || ''}>{tile}</Tooltip>;
}

// severityRowsFor returns the tile order: this engine's vocabulary first, then
// any label the data carries that the array does not know about. Every key of
// severityCounts therefore gets a tile, which is what keeps the breakdown summing
// to the total printed beneath it.
const severityRowsFor = (counts) => [
  ...SEVERITY_ORDER,
  ...Object.keys(counts).filter((s) => !SEVERITY_ORDER.includes(s)).sort(),
];

export default function InspectorDashboard({ config, onConfigChange }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [refreshJob, setRefreshJob] = useState(null);
  const pollRef = useRef(null);
  // Consecutive poll failures. GET /refresh/{id} rejects once a job ages out of
  // the table, and retrying that forever leaves a frozen bar and a permanently
  // disabled Refresh button; a few tolerated failures absorb a network blip
  // without turning a dead job into a dead page.
  const pollFailuresRef = useRef(0);
  // The two scalars the poll effect keys on, so a new job object carrying the
  // same id and status does not restart the interval.
  const jobId = refreshJob?.id;
  const jobRunning = refreshJob?.status === 'running';

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInspectorSummary();
      setSummary(res?.data || null);
      setError(null);
      setNotConfigured(false);
    } catch (e) {
      if (isNotConfigured(e)) {
        // Not a failure: the tenant disabled Inspector (possibly in another tab)
        // while this page was mounted. Rendering it as a red error would be a lie.
        setNotConfigured(true);
        setError(null);
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // On mount, discover a refresh already running server-side (started before a
  // navigation away or a full page reload) and re-attach to it — the poll effect
  // below then takes over exactly as if the user had just clicked Refresh.
  // Without this, anyone who reloads mid-refresh sees an idle page while the
  // fan-out is still running. A null `data` is idle, not an error; a failed
  // lookup is ignored rather than treated as "no job"; and a refresh started in
  // this session wins over whatever the lookup returns.
  useEffect(() => {
    let ignore = false;
    getInspectorActiveRefreshJob()
      .then((res) => {
        const job = res?.data;
        if (!ignore && job && job.status === 'running') {
          setRefreshJob((prev) => prev || job);
        }
      })
      .catch(() => { /* transient — the user can still refresh manually */ });
    return () => { ignore = true; };
  }, []);

  // Poll the running job; reload the summary once it settles.
  //
  // An interval owned by a ref, not the plan's self-rescheduling setTimeout with
  // an empty cleanup: that chain cannot be cancelled, so it keeps polling after
  // unmount and React's development double-mount starts a second chain that
  // fights the first.
  //
  // Keyed on the job's ID and a BOOLEAN, never on the job object. Every tick
  // stores a fresh object, so depending on it would tear the interval down and
  // rebuild it each cycle — the cadence drifts out to POLL_INTERVAL_MS + RTT and
  // the failure counter resets on every rebuild. Same rule the enrollment page
  // states.
  useEffect(() => {
    if (!jobId || !jobRunning) return undefined;
    pollFailuresRef.current = 0;
    const stop = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    pollRef.current = setInterval(async () => {
      try {
        const res = await getInspectorRefreshJob(jobId);
        const next = res?.data;
        pollFailuresRef.current = 0;
        setRefreshJob(next || null);
        // An empty body settles the job too. It is not a documented outcome —
        // the endpoint rejects for a job it cannot find — but treating it as
        // "still running" would leave the bar up forever with nothing behind it.
        if (!next || next.status !== 'running') {
          stop();
          await loadSummary();
        }
      } catch (_) {
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current >= 3) {
          stop();
          // Drop the job rather than leaving a bar stuck at its last value: the
          // button comes back and the summary shows whatever the run managed to
          // cache.
          setRefreshJob(null);
          loadSummary();
        }
      }
    }, POLL_INTERVAL_MS);
    return stop;
  }, [jobId, jobRunning, loadSummary]);

  const onRefresh = async () => {
    try {
      const res = await triggerInspectorRefresh();
      setRefreshJob(res?.data || null);
    } catch (e) {
      setError(e.message);
    }
  };

  // Hand the unconfigured case back to the entry gate, which is what decides
  // between the wizard and this page; re-reading the summary is the fallback when
  // this dashboard is rendered without that callback.
  const onRecheck = () => {
    if (onConfigChange) onConfigChange();
    else loadSummary();
  };

  const goFindings = (qs) => navigate(`/securityhub/inspector/findings${qs ? `?${qs}` : ''}`);

  // No admin account in the subtitle: Inspector uses the FLAT PEER model and has
  // no delegated administrator, so config carries no adminAccountId — cloning
  // Macie's header verbatim would print "Admin account undefined".
  const deploymentLine = (
    <>
      Home region <b>{summary?.homeRegion || config?.homeRegion || '—'}</b>
    </>
  );

  const header = (subtitle) => (
    <PortalPageHeader
      icon={<BugReportOutlinedIcon sx={{ fontSize: 24 }} />}
      title="Inspector Dashboard"
      color={INSPECTOR_COLOR}
      subtitle={subtitle}
    />
  );

  if (loading && !summary && !notConfigured) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header(deploymentLine)}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading Inspector posture…" color={INSPECTOR_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (notConfigured) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header(deploymentLine)}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalEmptyState
              icon={<BugReportOutlinedIcon sx={{ fontSize: 28 }} />}
              title="Amazon Inspector is not configured"
              description="Inspector is not enabled for this organization, or it was disabled while this page was open. Reload to pick the setup wizard back up."
              color={INSPECTOR_COLOR}
              action={(
                <Button variant="contained" sx={inspectorContainedSx} onClick={onRecheck}>
                  Reload
                </Button>
              )}
            />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header(deploymentLine)}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={loadSummary} />
        </motion.div>
      </motion.div>
    );
  }

  if (!summary) return null;

  // Normalize the keys before use: the tile order and the "unknown label" append
  // both compare strings, and a mixed-case label would otherwise produce a
  // permanently-zero tile plus an invisible duplicate.
  const counts = {};
  Object.entries(summary.severityCounts || {}).forEach(([k, v]) => {
    const key = String(k || '').toUpperCase();
    counts[key] = (counts[key] || 0) + Number(v || 0);
  });
  const severityRows = severityRowsFor(counts);
  // Summed over every key, not over SEVERITY_ORDER, so the total can never
  // disagree with the tiles above it.
  const totalFindings = Object.values(counts).reduce((n, v) => n + v, 0);

  const refreshing = jobRunning;
  // LastFindingsRefreshAt is a nullable pointer — the backend sends null rather
  // than a zero time precisely so this does not render as "January year 1".
  const lastRefreshText = summary.lastFindingsRefreshAt
    ? (relativeTime(summary.lastFindingsRefreshAt) || 'unknown')
    : 'never';
  const resourceTypes = summary.resourceTypes || [];
  const topCves = summary.topCves || [];

  // Partial success is the NORMAL outcome of the flat peer fan-out — one
  // unreachable account does not fail the run — so a completed job that left gaps
  // must not render identically to a clean one.
  const accountsFailed = refreshJob?.accountsFailed || 0;
  const unitsUnscanned = refreshJob?.unitsUnscanned || 0;
  const degraded = refreshJob?.status === 'completed'
    && (accountsFailed > 0 || unitsUnscanned > 0 || !!refreshJob?.note);

  const coverageCards = [
    {
      key: 'fixes',
      label: 'Fixes available',
      value: numeric(summary.fixAvailableCount),
      color: CK.green,
      icon: <BuildCircleOutlinedIcon sx={{ fontSize: 28 }} />,
      caption: 'findings a vendor fix already covers',
      // No findings link: the list has no fix-available filter, so a click would
      // silently open an unfiltered list and misrepresent this number.
    },
    {
      key: 'accounts',
      label: 'Accounts covered',
      value: numeric(summary.accountCount),
      color: CK.blue,
      icon: <AccountTreeIcon sx={{ fontSize: 28 }} />,
      caption: 'enrolled and scanning',
      onClick: () => navigate('/securityhub/inspector/enrollment'),
    },
    {
      key: 'regions',
      label: 'Regions covered',
      value: numeric(summary.regionCount),
      color: CK.purple,
      icon: <PublicIcon sx={{ fontSize: 28 }} />,
      caption: 'linked regions with an enabled scan',
      onClick: () => navigate('/securityhub/inspector/settings'),
    },
  ];

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      {/* === Header === */}
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<BugReportOutlinedIcon sx={{ fontSize: 24 }} />}
          title="Inspector Dashboard"
          color={INSPECTOR_COLOR}
          subtitle={(
            <>
              {deploymentLine}
              {' • '}<b>{summary.accountCount}</b> account{summary.accountCount === 1 ? '' : 's'} across{' '}
              <b>{summary.regionCount}</b> region{summary.regionCount === 1 ? '' : 's'}
              {' • '}Last refresh: <b>{lastRefreshText}</b>
              {refreshJob?.status === 'completed' && !degraded && (
                <> • {numeric(refreshJob.findingsFetched)} findings fetched</>
              )}
            </>
          )}
          action={(
            <Tooltip title={refreshing ? 'Refresh already in progress' : 'Fetch the latest findings from AWS'}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  disabled={refreshing}
                  onClick={onRefresh}
                  sx={inspectorContainedSx}
                >
                  {refreshing ? 'Refreshing…' : 'Refresh findings'}
                </Button>
              </span>
            </Tooltip>
          )}
        />
      </motion.div>

      {/* Determinate bar: Inspector counts (account, region) work units upfront,
          so progress is exact rather than the estimate the other capabilities
          fall back to. progressLabel is composed by the backend so the bar and
          its caption cannot drift; the line below adds only what the label does
          not carry. */}
      {refreshing && (
        <motion.div variants={sectionVariants}>
          <Box sx={{ ...insetPanelSx, mb: 3 }}>
            <OperationProgress
              percent={refreshJob?.progress}
              label={refreshJob?.progressLabel || 'Refreshing findings…'}
              sx={{ mb: 1 }}
            />
            <Typography sx={captionSx}>
              {numeric(refreshJob?.findingsFetched)} findings fetched so far
              {accountsFailed > 0 && (
                <> · <b>{accountsFailed}</b> account{accountsFailed === 1 ? '' : 's'} could not be reached</>
              )}
            </Typography>
          </Box>
        </motion.div>
      )}

      {degraded && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY, '& .MuiAlert-message': { fontFamily: FONT_BODY } }}
          >
            Refresh finished with gaps: {numeric(refreshJob.findingsFetched)} findings fetched
            {accountsFailed > 0 && <>, {accountsFailed} of {refreshJob.accountsTotal} accounts unreachable</>}
            {unitsUnscanned > 0 && <>, {unitsUnscanned} account-region {unitsUnscanned === 1 ? 'unit' : 'units'} not scanned</>}
            . The counts below cover only what was scanned.
            {refreshJob.note && <> {refreshJob.note}</>}
          </Alert>
        </motion.div>
      )}

      {refreshJob?.status === 'failed' && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState
            error={refreshJob.errorMessage || 'The findings refresh failed.'}
            onRetry={onRefresh}
          />
        </motion.div>
      )}

      {/* partial_failure is the terminal status of ANY partly-successful
          operation — the initial enable, a scan-type or region update, an
          add-member run, and a failed teardown, whose errorMessage reads
          "teardown failed for: …". Naming the enable specifically would caption
          half of those wrongly, so the copy describes the state the accounts are
          left in and lets errorMessage say which operation produced it. */}
      {config?.status === 'partial_failure' && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY, '& .MuiAlert-message': { fontFamily: FONT_BODY } }}
            action={(
              <Button
                size="small"
                onClick={() => navigate('/securityhub/inspector/enrollment')}
                sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: 'inherit' }}
              >
                Review
              </Button>
            )}
          >
            Some accounts are not fully enrolled in Amazon Inspector. {config.errorMessage}
          </Alert>
        </motion.div>
      )}

      {/* === Severity stat cards ===
          No motion wrapper: the tiles animate themselves in pure CSS, and a
          motion component here can write an inline opacity onto them once its
          own animation settles. */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        {severityRows.map((sev, i) => {
          const n = counts[sev] || 0;
          const meta = severityMeta[sev] || {};
          const color = severityColor(sev);
          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={sev}>
              <div style={tileEntranceStyle(i)}>
                <StatTile
                  icon={meta.icon || <InfoOutlinedIcon sx={{ fontSize: 28 }} />}
                  value={n.toLocaleString()}
                  color={color}
                  label={sev.charAt(0) + sev.slice(1).toLowerCase()}
                  caption={n === 1 ? 'active finding' : 'active findings'}
                  hint={meta.hint}
                  onClick={() => goFindings(`severity=${encodeURIComponent(sev)}`)}
                />
              </div>
            </Grid>
          );
        })}
      </Grid>

      <motion.div variants={sectionVariants}>
        <Typography sx={{ ...captionSx, mb: 3 }}>
          Total active findings across all severities: <b>{numeric(totalFindings)}</b>
        </Typography>
      </motion.div>

      {/* === Coverage stat row === */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        {coverageCards.map((c, i) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={c.key}>
            <div style={tileEntranceStyle(i)}>
              <StatTile
                icon={c.icon}
                value={c.value}
                color={c.color}
                label={c.label}
                caption={c.caption}
                onClick={c.onClick}
              />
            </div>
          </Grid>
        ))}
      </Grid>

      <motion.div variants={sectionVariants}>
        <Typography sx={{ ...captionSx, mb: 3 }}>
          Resource types scanned:{' '}
          <b>{resourceTypes.length ? resourceTypes.join(' · ') : 'none selected'}</b>
        </Typography>
      </motion.div>

      {/* === Top CVEs ===
          Inspector's distinctive surface: the "what do I patch first" view no
          other capability can produce. */}
      <motion.div variants={sectionVariants}>
        <PortalSectionCard
          icon={<CoronavirusOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Top CVEs"
          subtitle="Most prevalent vulnerabilities across the estate — patch these first"
          color={INSPECTOR_COLOR}
          action={(
            <Chip size="small" label={`${topCves.length} shown`} sx={statusChipSx('info')} />
          )}
          sx={{ mb: 3 }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>CVE</TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 140 }}>Findings</TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 140 }}>
                    <Tooltip title="Highest Inspector score across this CVE's findings. An em dash means AWS reported no score.">
                      <span>Max score</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 160 }}>Fix available</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topCves.map((row, index) => {
                  const fix = FIX_META[String(row.fixAvailable || '').toUpperCase()];
                  return (
                    <TableRow
                      key={row.cveId}
                      hover
                      sx={{ ...rowSx(index), cursor: 'pointer' }}
                      // The findings search matches cve_id, so this opens exactly
                      // this CVE's findings.
                      onClick={() => goFindings(`search=${encodeURIComponent(row.cveId)}`)}
                    >
                      <TableCell sx={{ ...bodyCellSx, fontWeight: 600, wordBreak: 'break-word' }}>
                        {row.cveId}
                      </TableCell>
                      <TableCell align="right" sx={bodyCellSx}>
                        <Chip
                          label={numeric(row.count)}
                          size="small"
                          sx={{ ...statusChipSx(row.count > 0 ? 'warning' : 'neutral'), minWidth: 70 }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ ...bodyCellSx, fontWeight: 600 }}>
                        {formatScore(row.maxScore)}
                      </TableCell>
                      <TableCell align="right" sx={bodyCellSx}>
                        <Tooltip title={fix?.hint || ''}>
                          {/* An em dash, never an invented "Unknown": AWS omits
                              fixAvailable rather than reporting a value, and the
                              detail body omits the field entirely for the same
                              reason. Inventing a label would read as a real answer. */}
                          <Chip
                            label={fix?.label || row.fixAvailable || '—'}
                            size="small"
                            sx={{ ...statusChipSx(fix?.intent || 'neutral'), minWidth: 70 }}
                          />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {topCves.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <PortalEmptyState
                        icon={<CoronavirusOutlinedIcon sx={{ fontSize: 28 }} />}
                        title="No CVEs recorded yet"
                        description="Only package-vulnerability findings carry a CVE — network-reachability findings never will. Run “Refresh findings” to pull the latest results from AWS."
                        color={INSPECTOR_COLOR}
                        py={2}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </PortalSectionCard>
      </motion.div>

      {/* === Finding types === */}
      <motion.div variants={sectionVariants}>
        <PortalSectionCard
          icon={<CategoryOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Findings by type"
          subtitle="Select a type to open the matching findings"
          color={INSPECTOR_COLOR}
          action={(
            <Chip
              size="small"
              label={`${(summary.perType || []).length} types`}
              sx={statusChipSx('info')}
            />
          )}
          sx={{ mb: 3 }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>Type</TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 140 }}>Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(summary.perType || []).map((row, index) => (
                  <TableRow
                    key={row.type}
                    hover
                    sx={{ ...rowSx(index), cursor: 'pointer' }}
                    onClick={() => goFindings(`type=${encodeURIComponent(row.type)}`)}
                  >
                    <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{row.type}</TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      <Chip
                        label={numeric(row.count)}
                        size="small"
                        sx={{ ...statusChipSx(row.count > 0 ? 'warning' : 'neutral'), minWidth: 70 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {(!summary.perType || summary.perType.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <PortalEmptyState
                        icon={<CategoryOutlinedIcon sx={{ fontSize: 28 }} />}
                        title="No findings detected yet"
                        description="Run “Refresh findings” to pull the latest Inspector results from AWS."
                        color={INSPECTOR_COLOR}
                        py={2}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </PortalSectionCard>
      </motion.div>

      {/* === Per-account table === */}
      <motion.div variants={sectionVariants}>
        <PortalSectionCard
          icon={<AccountTreeIcon sx={{ fontSize: 20 }} />}
          title="Per-account findings"
          subtitle="Select an account to open the matching findings"
          color={INSPECTOR_COLOR}
          action={(
            <Chip
              size="small"
              label={`${(summary.perAccount || []).length} accounts with findings`}
              sx={statusChipSx('info')}
            />
          )}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>Account</TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 180 }}>Active findings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(summary.perAccount || []).map((row, index) => (
                  <TableRow
                    key={row.accountId}
                    hover
                    sx={{ ...rowSx(index), cursor: 'pointer' }}
                    onClick={() => goFindings(`account=${encodeURIComponent(row.accountId)}`)}
                  >
                    <TableCell sx={bodyCellSx}>{row.accountId}</TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      <Chip
                        icon={<WarningAmberIcon />}
                        label={numeric(row.count)}
                        size="small"
                        sx={{ ...statusChipSx(row.count > 0 ? 'warning' : 'neutral'), minWidth: 80 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {(!summary.perAccount || summary.perAccount.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <PortalEmptyState
                        icon={<AccountTreeIcon sx={{ fontSize: 28 }} />}
                        title="No accounts with active findings"
                        description="Enrolled accounts appear here as soon as Inspector reports a finding for them."
                        color={INSPECTOR_COLOR}
                        py={2}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </PortalSectionCard>
      </motion.div>
    </motion.div>
  );
}
